import { useEffect, useMemo, useState } from 'react';
import { FaSave } from 'react-icons/fa';
import { CONTENT_GROUPS, ALL_CONTENT_ITEMS, defaultText } from '../../lib/content';
import { fetchContentMap } from '../../lib/api';
import { saveContent } from '../../lib/adminApi';

const ContentManagementPage = () => {
  // values: { [key]: { pl, en } }
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const labels = useMemo(() => {
    const m = {};
    for (const it of ALL_CONTENT_ITEMS) m[it.key] = it.label;
    return m;
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      let dbMap = {};
      try { dbMap = await fetchContentMap(); } catch { /* ignore */ }
      const init = {};
      for (const it of ALL_CONTENT_ITEMS) {
        init[it.key] = {
          pl: dbMap[it.key]?.pl ?? defaultText(it.key, 'pl') ?? '',
          en: dbMap[it.key]?.en ?? defaultText(it.key, 'en') ?? '',
        };
      }
      setValues(init);
      setLoading(false);
    })();
  }, []);

  const set = (key, lang, v) => {
    setValues((p) => ({ ...p, [key]: { ...p[key], [lang]: v } }));
    setDirty((p) => ({ ...p, [key]: true }));
    setMsg(null);
  };

  const handleSave = async () => {
    const rows = Object.keys(dirty)
      .filter((k) => dirty[k])
      .map((key) => ({ key, label: labels[key] || key, value_pl: values[key].pl, value_en: values[key].en }));
    if (!rows.length) { setMsg({ type: 'ok', text: 'Brak zmian do zapisania.' }); return; }
    setSaving(true);
    setMsg(null);
    try {
      await saveContent(rows);
      setDirty({});
      setMsg({ type: 'ok', text: `Zapisano ${rows.length} ${rows.length === 1 ? 'tekst' : 'tekstów'}. Zmiany są widoczne na stronie po odświeżeniu.` });
    } catch (err) {
      setMsg({ type: 'err', text: 'Błąd zapisu: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.values(dirty).filter(Boolean).length;

  if (loading) return <p className="text-gray-500">Ładowanie...</p>;

  return (
    <div className="max-w-4xl pb-24">
      <p className="text-gray-600 mb-6">
        Edytuj stałe teksty stron (dwujęzycznie PL / EN). Zmiany zapisują się w bazie i nadpisują domyślne teksty.
      </p>

      {CONTENT_GROUPS.map((group) => (
        <div key={group.section} className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="px-5 py-3 border-b bg-gray-50 rounded-t-xl">
            <h3 className="font-semibold text-gray-800">{group.section}</h3>
          </div>
          <div className="p-5 space-y-5">
            {group.items.map((it) => {
              const val = values[it.key] || { pl: '', en: '' };
              const Field = it.multiline ? 'textarea' : 'input';
              return (
                <div key={it.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {it.label} {dirty[it.key] && <span className="text-brand-accent text-xs">(zmienione)</span>}
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <span className="text-xs text-gray-400">Polski</span>
                      <Field
                        value={val.pl}
                        onChange={(e) => set(it.key, 'pl', e.target.value)}
                        rows={it.multiline ? 4 : undefined}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">English</span>
                      <Field
                        value={val.en}
                        onChange={(e) => set(it.key, 'en', e.target.value)}
                        rows={it.multiline ? 4 : undefined}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white/95 backdrop-blur border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-4 z-40">
        {msg && (
          <span className={`text-sm ${msg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{msg.text}</span>
        )}
        <button
          onClick={handleSave}
          disabled={saving || dirtyCount === 0}
          className="inline-flex items-center gap-2 bg-brand-accent text-white px-5 py-2 rounded-lg hover:opacity-90 disabled:opacity-40"
        >
          <FaSave /> {saving ? 'Zapisywanie...' : `Zapisz zmiany${dirtyCount ? ` (${dirtyCount})` : ''}`}
        </button>
      </div>
    </div>
  );
};

export default ContentManagementPage;
